import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle } from "lucide-react";
import { registrationAPI } from "@/services/api";

type Outcome =
  | { kind: "loading" }
  | { kind: "checked-in"; name: string; event: string; at: string; repeat: boolean }
  | { kind: "error"; message: string };

/**
 * Opened by an admin scanning a member's QR code with their phone camera.
 * The token in the URL is exchanged for an attendance record.
 */
export default function CheckIn() {
  const { token } = useParams<{ token: string }>();
  const [outcome, setOutcome] = useState<Outcome>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setOutcome({ kind: "error", message: "No check-in code in this link." });
      return;
    }

    let cancelled = false;

    registrationAPI
      .checkInByToken(token)
      .then((data) => {
        if (cancelled) return;
        const attendee = data.attendee || {};
        setOutcome({
          kind: "checked-in",
          name: [attendee.firstName, attendee.lastName].filter(Boolean).join(" ") || "Attendee",
          event: data.event?.title || "the event",
          at: data.checkedInAt
            ? new Date(data.checkedInAt).toLocaleString("en-IN", {
                hour: "numeric",
                minute: "2-digit",
                day: "numeric",
                month: "short",
              })
            : "",
          repeat: !!data.alreadyCheckedIn,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setOutcome({
          kind: "error",
          message:
            err?.response?.status === 403
              ? "Only admins can check people in. Sign in with an admin account and scan again."
              : err?.response?.data?.message || "That code could not be read. Please try again.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-sm rounded-lg border border-border bg-white p-8 text-center shadow-lg">
        {outcome.kind === "loading" && (
          <>
            <Clock className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-600">Checking in...</p>
          </>
        )}

        {outcome.kind === "checked-in" && (
          <>
            <CheckCircle2
              className={`mx-auto h-14 w-14 ${outcome.repeat ? "text-amber-500" : "text-green-600"}`}
            />
            <h1 className="mt-4 text-2xl font-bold text-secondary">
              {outcome.repeat ? "Already checked in" : "Checked in"}
            </h1>
            <p className="mt-2 text-lg font-medium text-secondary">{outcome.name}</p>
            <p className="text-sm text-gray-600">{outcome.event}</p>
            {outcome.at && (
              <p className="mt-3 text-xs text-gray-500">
                {outcome.repeat ? "First checked in at" : "Checked in at"} {outcome.at}
              </p>
            )}
          </>
        )}

        {outcome.kind === "error" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-600" />
            <h1 className="mt-4 text-xl font-bold text-secondary">Could not check in</h1>
            <p className="mt-2 text-sm text-gray-600">{outcome.message}</p>
          </>
        )}

        <Link
          to="/admin/dashboard"
          className="mt-8 inline-block text-sm font-medium text-primary hover:text-primary-dark"
        >
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
